import { useState, useRef } from "react";
import { Paperclip, Upload, Trash2, FileText, Loader2, Lock, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiGet, apiPost, apiDelete } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const MAX_FILE_BYTES = 6 * 1024 * 1024; // 6 MB

interface Attachment {
  id: string;
  companyId: string;
  name: string;
  url: string;
  mimeType?: string;
  size?: number;
  uploadedBy?: string;
  createdAt?: string;
}

interface AttachmentPanelProps {
  entityType: string;
  entityId: string;
  companyId: string;
  enabled?: boolean;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(mimeType?: string) {
  if (!mimeType) return <FileText className="w-4 h-4 text-slate-400" />;
  if (mimeType.startsWith("image/")) return <FileText className="w-4 h-4 text-emerald-500" />;
  if (mimeType === "application/pdf") return <FileText className="w-4 h-4 text-red-400" />;
  return <FileText className="w-4 h-4 text-slate-400" />;
}

export function AttachmentPanel({
  entityType,
  entityId,
  companyId,
  enabled = true,
}: AttachmentPanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const queryKey = ["attachments", companyId, entityType, entityId];

  const { data: attachments = [], isLoading } = useQuery<Attachment[]>({
    queryKey,
    queryFn: () => {
      const q = new URLSearchParams({ companyId, entityType, entityId });
      return apiGet(`/attachments?${q}`);
    },
    enabled: enabled && !!entityId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/attachments/${id}?companyId=${companyId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Attachment removed" });
    },
    onError: () => toast({ title: "Failed to delete attachment", variant: "destructive" }),
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (file.size > MAX_FILE_BYTES) {
      toast({
        title: "File too large",
        description: `Maximum file size is 6 MB. This file is ${formatBytes(file.size)}.`,
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const urlRes = await fetch("/api/storage/uploads/request-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type || "application/octet-stream" }),
      });
      if (!urlRes.ok) throw new Error("Failed to get upload URL");
      const { uploadURL, objectPath } = await urlRes.json();

      const putRes = await fetch(uploadURL, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!putRes.ok) throw new Error("Upload to storage failed");

      await apiPost("/attachments", {
        id: crypto.randomUUID(),
        companyId,
        entityType,
        entityId,
        name: file.name,
        url: `/api/storage${objectPath}`,
        mimeType: file.type || "application/octet-stream",
        size: file.size,
      });

      queryClient.invalidateQueries({ queryKey });
      toast({ title: "File attached", description: file.name });
    } catch {
      toast({ title: "Upload failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  if (!enabled) {
    return (
      <div className="rounded-xl border border-dashed border-border/50 p-4 flex items-center gap-3 text-muted-foreground bg-muted/20">
        <Lock className="w-4 h-4 shrink-0" />
        <div>
          <div className="text-sm font-medium text-foreground/70">File attachments</div>
          <div className="text-xs mt-0.5">Upgrade your plan to attach files to documents</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-sm font-medium text-foreground/80">
          <Paperclip className="w-3.5 h-3.5" />
          Attachments
          {attachments.length > 0 && (
            <span className="text-xs text-muted-foreground ml-1">({attachments.length})</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">max 6 MB per file</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2.5 text-xs rounded-lg"
            disabled={uploading || !entityId}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Uploading…</>
            ) : (
              <><Upload className="w-3 h-3 mr-1" /> Attach file</>
            )}
          </Button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        accept="*/*"
      />

      {!entityId && (
        <p className="text-xs text-muted-foreground italic">Save the document first to add attachments.</p>
      )}

      {isLoading && entityId && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      )}

      {!isLoading && attachments.length === 0 && entityId && (
        <p className="text-xs text-muted-foreground italic">No attachments yet.</p>
      )}

      {attachments.length > 0 && (
        <div className="space-y-1.5">
          {attachments.map(att => (
            <div
              key={att.id}
              className={cn(
                "flex items-center gap-2.5 px-3 py-2 rounded-xl border border-border/40 bg-muted/20 group",
              )}
            >
              {fileIcon(att.mimeType)}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-foreground truncate">{att.name}</div>
                {att.size != null && (
                  <div className="text-[10px] text-muted-foreground">{formatBytes(att.size)}</div>
                )}
              </div>
              <a
                href={att.url}
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(att.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
